import { BUSINESS, ORDER_STATUS_LABELS, SCHEDULES } from '@inflip/shared';

import { BodyRow, PageHeader, TableShell, Td, Th, TheadRow } from '../components/ui';

/**
 * 어드민 가이드북 — 각 관리 화면의 상태(enum)가 무엇을 뜻하고,
 * 어떤 액션이 상태를 어디로 보내는지 한 페이지에 정리.
 * 상단 헤더의 "도움말" 버튼으로 진입.
 */

interface StatusRow {
  code: string;
  label: string;
  desc: string;
  tone?: 'red' | 'green' | 'blue' | 'amber';
}

const TONE_CLASS: Record<NonNullable<StatusRow['tone']> | 'default', string> = {
  red: 'bg-red-100 text-red-700',
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-800',
  default: 'bg-muted text-muted-foreground',
};

const SECTIONS: {
  id: string;
  title: string;
  intro?: string;
  rows: StatusRow[];
  /** 정상 흐름(happy path) 코드 순서 + 분기 설명 — 표 위에 순서도로 렌더 */
  flow?: { happy: (keyof typeof ORDER_STATUS_LABELS)[]; branches: string[] };
  notes?: string[];
}[] = [
  {
    id: 'order',
    title: '주문 (Order)',
    intro:
      '주문은 "계약"의 상태만 갖습니다. 돈의 움직임은 결제(Payment)·환불(Refund)에, 지급은 정산(Settlement)에 따로 기록됩니다.',
    rows: [
      {
        code: 'ORDER_CREATED',
        label: ORDER_STATUS_LABELS.ORDER_CREATED,
        desc: '구매자가 주문을 만든 직후. 시스템이 곧바로 입금 대기로 보냅니다.',
      },
      {
        code: 'PAYMENT_PENDING',
        label: ORDER_STATUS_LABELS.PAYMENT_PENDING,
        desc: '무통장: 입금 대기 / 카드: 승인 대기. 기한 내 미입금이면 스케줄러가 자동 취소합니다.',
        tone: 'amber',
      },
      {
        code: 'PAID',
        label: ORDER_STATUS_LABELS.PAID,
        desc: '어드민이 입금 확인(대사)을 하면 이 상태가 됩니다. 이때 거래 채팅방이 열리고, 판매자에게 발급 요청 알림이 갑니다.',
        tone: 'blue',
      },
      {
        code: 'LICENSE_ISSUED',
        label: ORDER_STATUS_LABELS.LICENSE_ISSUED,
        desc: '판매자가 시작일을 정해 라이선스 발급 (어드민도 개입 발급 가능) — 발급이 곧 거래 완료 시점. 만료일은 시작일 + 계약 기간. 구매자가 구매확정하거나 구매확정 기한(결제 설정)이 지나 자동 확정되면 정산 대기로 넘어갑니다.',
        tone: 'green',
      },
      {
        code: 'SETTLEMENT_PENDING',
        label: ORDER_STATUS_LABELS.SETTLEMENT_PENDING,
        desc: '구매확정되면 정산 건이 만들어져 이 상태. 정산 화면에서 지급을 진행합니다.',
        tone: 'amber',
      },
      {
        code: 'PAYOUT_COMPLETED',
        label: ORDER_STATUS_LABELS.PAYOUT_COMPLETED,
        desc: '판매자에게 지급 완료. 이후 환불은 판매자에게서 먼저 회수한 뒤에만 가능.',
        tone: 'green',
      },
      {
        code: 'DISPUTED',
        label: ORDER_STATUS_LABELS.DISPUTED,
        desc: '구매자/어드민이 분쟁 제기. 채팅방에 어드민이 자동 합류하며, 기각(정산 진행)·환불·계약 해지로 종결됩니다.',
        tone: 'red',
      },
      {
        code: 'TERMINATED',
        label: ORDER_STATUS_LABELS.TERMINATED,
        desc: '어드민이 계약을 중도 해지. 선불 처리(비환불·부분 환불(선불)·전액 환불(선불))를 함께 고르고, 상태 옆 배지로 보입니다. 해지 시각까지의 사용은 인정되고(라이선스 "해지됨", 증서 계속 발급), 해지일이 있는 달까지 로열티를 보고받습니다. 정산은 해지 뒤에도 지급합니다.',
        tone: 'red',
      },
      {
        code: 'REFUNDED',
        label: ORDER_STATUS_LABELS.REFUNDED,
        desc: '전액 환불이 완료된 주문. 라이선스는 회수(revoke)됩니다.',
        tone: 'red',
      },
      {
        code: 'CANCELLED',
        label: ORDER_STATUS_LABELS.CANCELLED,
        desc: '결제 전 취소 (구매자/어드민/기한 만료). 돈이 움직인 적 없는 종결 상태.',
      },
    ],
    flow: {
      happy: [
        'ORDER_CREATED',
        'PAYMENT_PENDING',
        'PAID',
        'LICENSE_ISSUED',
        'SETTLEMENT_PENDING',
        'PAYOUT_COMPLETED',
      ],
      branches: [
        '취소됨: 입금 대기까지만 가능 — 구매자/어드민 취소 또는 기한 만료(시스템). 돈이 움직인 적 없는 종결.',
        '분쟁 처리 중: 거래 완료(발급)·정산 대기에서 구매자/어드민이 제기 → 환불됨 또는 정산 완료로만 종결.',
        '환불됨: 결제 확인 이후 어느 단계에서든 어드민이 처리. 정산 완료 후에는 승인 때 판매자 회수 요청(기한 14일)이 가고, 회수를 확인해야 송금·지급 완료할 수 있습니다. 계약을 처음부터 무효로 할 때도 이것을 씁니다.',
        '계약 해지: 거래 완료(발급)·정산 대기·정산 완료·분쟁 처리 중에서 어드민이 처리 (선불 0원 계약은 결제 완료에서도). 구매확정 전이면 해지와 함께 정산을 만들고, 해지와 함께 고른 환불은 송금 뒤 지급 완료를 눌러 끝냅니다.',
      ],
    },
    notes: [
      '상태 전이는 항상 정해진 표(OrderStateMachine)를 따릅니다 — 표에 없는 전이는 시스템이 거부합니다.',
      '주체: 입금 확인·환불·지급은 어드민, 라이선스 발급은 판매자(어드민 개입 가능), 입금 만료·정산 대기 진입은 스케줄러(시스템), 취소·분쟁 제기는 구매자도 가능합니다.',
    ],
  },
  {
    id: 'payment',
    title: '결제 (Payment)',
    intro: '주문 1건에 결제 시도가 여러 번 있을 수 있어 별도 상태를 갖습니다.',
    rows: [
      {
        code: 'PENDING',
        label: '대기',
        desc: '무통장: 입금 대기 / 카드: 승인 대기.',
        tone: 'amber',
      },
      {
        code: 'CONFIRMED',
        label: '확인됨',
        desc: '어드민 입금 확인 또는 PG 승인 완료. 주문이 결제 완료로 넘어갑니다.',
        tone: 'green',
      },
      { code: 'FAILED', label: '실패', desc: 'PG 승인 실패 (무통장에는 없음).', tone: 'red' },
      {
        code: 'EXPIRED',
        label: '만료',
        desc: '입금 기한 내 미입금 — 스케줄러가 자동 처리.',
        tone: 'red',
      },
      { code: 'CANCELLED', label: '취소', desc: '주문 취소에 따라 결제도 취소.' },
    ],
    notes: ['결제 묶음(그룹) 주문은 입금 1건 확인으로 소속 주문 전체가 함께 확인됩니다.'],
  },
  {
    id: 'refund',
    title: '환불 (Refund)',
    intro: '부분 환불을 지원합니다 — 환불 합계(진행 중 + 완료)는 결제 금액을 넘을 수 없습니다.',
    rows: [
      {
        code: 'REQUESTED',
        label: '요청됨',
        desc: '구매자 요청 또는 어드민 직권 생성. 승인/거절 대기.',
        tone: 'amber',
      },
      {
        code: 'APPROVED',
        label: '승인',
        desc: '어드민 승인 — 실제 송금(무통장) 또는 PG 취소 대기.',
        tone: 'blue',
      },
      {
        code: 'COMPLETED',
        label: '완료',
        desc: '환불금 지급 완료. 전액 환불이 완성되면 주문이 환불 완료로 넘어가고 라이선스가 회수됩니다.',
        tone: 'green',
      },
      {
        code: 'REJECTED',
        label: '거절',
        desc: '요청된 환불을 어드민이 사유와 함께 거절.',
        tone: 'red',
      },
      {
        code: 'CANCELLED',
        label: '취소',
        desc: '어드민이 승인·만든 환불을 송금 전에 거둬들임 (승인 취소, 계약 해지 취소). 구매자 요청을 거절한 것과 구분합니다.',
      },
    ],
    notes: ['무통장 환불은 수취 계좌 정보가 필수입니다.'],
  },
  {
    id: 'settlement',
    title: '정산 (Settlement)',
    intro:
      '라이선스 발급 후 구매자가 구매확정하거나 구매확정 기한(결제 설정)이 지나 자동 확정된 주문에 대해 정산 건이 만들어집니다.',
    rows: [
      { code: 'SCHEDULED', label: '예정', desc: '정산 건 생성됨 — 지급 예정 상태.', tone: 'amber' },
      {
        code: 'HOLDING',
        label: '보류기간',
        desc: '분쟁·환불 가능성에 대비한 대기 기간 중.',
        tone: 'amber',
      },
      {
        code: 'READY',
        label: '지급 가능',
        desc: '보류기간 종료 — 지급 실행만 남음.',
        tone: 'blue',
      },
      {
        code: 'PAID_OUT',
        label: '지급 완료',
        desc: '판매자 계좌로 송금 완료. 원장에 PAYOUT_SENT 가 함께 기록됩니다.',
        tone: 'green',
      },
      {
        code: 'ON_HOLD',
        label: '어드민 보류',
        desc: '어드민이 사유와 함께 수동 보류 — 분쟁 조사 등.',
        tone: 'red',
      },
      { code: 'CANCELLED', label: '취소', desc: '환불 등으로 정산이 무효화됨.' },
    ],
    notes: [
      '정산 지급 뒤 환불: 환불을 승인하면 판매자에게 환불할 몫(순정산액 × 환불 비율, 전액 환불이면 남은 지급액 전부)의 회수 요청이 갑니다. 기한은 14일, 입금 계좌·불이행 시 조치가 알림에 함께 안내됩니다.',
      '판매자가 플랫폼 계좌로 입금하면 주문 상세 환불 내역에서 [회수 확인] — 원장에 PAYOUT_CLAWBACK 이 기록되고, 그다음 구매자에게 송금하고 [지급 완료 처리]를 누릅니다. 플랫폼이 먼저 돌려주지 않습니다.',
      '기한이 지나면 판매자 계정 접속이 자동 정지됩니다(다른 사유로 이미 정지된 계정은 그대로). 그 뒤 입금을 확인하면 그 정지만 풀립니다. 환불 승인을 취소하면 회수 요청도 취소됩니다(이미 회수했으면 취소 불가).',
    ],
  },
  {
    id: 'royalty',
    title: '로열티 (Royalty Report)',
    intro:
      '로열티 계약은 지급일(매달 N일)을 계약 조건으로 포함합니다. 구매자는 매달 지급일까지 전월 매출을 보고하고 로열티를 입금합니다.',
    rows: [
      {
        code: 'REPORTED',
        label: '신고됨',
        desc: '구매자(사용자)가 매출/사용 실적을 신고한 상태.',
        tone: 'amber',
      },
      { code: 'CONFIRMED', label: '확정', desc: '어드민이 신고 내용을 검수·확정.', tone: 'blue' },
      { code: 'PAID_OUT', label: '지급 완료', desc: '판매자에게 로열티 지급 완료.', tone: 'green' },
    ],
    notes: [
      `지급일 ${BUSINESS.ROYALTY_REPORT_REMINDER_DAYS}일 전부터 구매자에게 보고 리마인더가, 지급일 경과 시 구매자 연체 알림 + 판매자 지연 알림이 자동 발송됩니다 (스케줄러 ${SCHEDULES.ROYALTY_REPORT_REMINDERS.label}).`,
      '지급일 도입 전 체결된 계약은 지급일이 없어 자동 독촉 대상이 아닙니다.',
    ],
  },
  {
    id: 'chat',
    title: '채팅 관리',
    intro: '채팅방은 두 종류입니다. 목록의 유형 배지로 구분됩니다.',
    rows: [
      {
        code: 'ORDER (거래)',
        label: '거래 채팅',
        desc: '입금 확인(PAID) 시점에 결제 묶음(OrderGroup)당 1개 자동 개설. 묶음의 주문별 상태 변화가 [권리명] 라벨이 붙은 SYSTEM 메시지로 기록됩니다.',
        tone: 'blue',
      },
      {
        code: 'DIRECT (일반)',
        label: '일반 1:1',
        desc: '(판매자, 구매자) 쌍당 1개. 리스팅 "문의하기"와 프로필 "대화하기"가 같은 방으로 모입니다. 주문이 없으므로 주문 상태는 "—".',
      },
      {
        code: 'OPEN / CLOSED',
        label: '방 상태',
        desc: 'OPEN 은 대화 가능, CLOSED 는 종결된 방(현재는 자동 종결 없음).',
      },
      {
        code: 'BUYER / SELLER / ADMIN',
        label: '참여자 역할',
        desc: '구매자·판매자 외에 어드민이 참여자로 들어갈 수 있습니다.',
      },
    ],
    notes: [
      '분쟁(DISPUTED) 전이 시 어드민 1명이 거래 채팅에 자동 합류합니다.',
      '그 외 방도 채팅방 상세의 "대화 참여" 버튼으로 직접 합류해 양측과 대화할 수 있습니다. 참여 전에는 열람만 가능합니다.',
      '"어드민" 컬럼이 "참여 중"이면 이미 어드민이 합류한 방입니다.',
    ],
  },
  {
    id: 'listing',
    title: '리스팅',
    rows: [
      { code: 'DRAFT', label: '작성 중', desc: '판매자가 아직 제출하지 않은 초안.' },
      {
        code: 'PENDING_REVIEW',
        label: '심사 대기',
        desc: '판매자가 제출 — 어드민 승인(공개) 또는 반려 대상.',
        tone: 'amber',
      },
      {
        code: 'PUBLISHED',
        label: '공개',
        desc: '마켓에 노출 중. 사용자가 구매·문의할 수 있습니다.',
        tone: 'green',
      },
      {
        code: 'SUSPENDED',
        label: '중지',
        desc: '어드민이 노출 중지 — 신고·정책 위반 등.',
        tone: 'red',
      },
      {
        code: 'ENDED',
        label: '판매 종료',
        desc: '판매자가 내린 리스팅. 판매자가 [판매 재개]하거나 고쳐 저장하면 심사 대기로 다시 들어온다 — 목록에 "판매 재개" 표시, 펼치면 처음 공개·판매 종료·중지 이력·거래 수.',
      },
    ],
  },
];

export function GuidePage() {
  return (
    <main className="mx-auto w-full max-w-4xl px-6 py-8">
      <PageHeader
        title="어드민 가이드북"
        description="각 관리 화면의 상태가 무엇을 뜻하는지, 어떤 액션이 다음 상태로 보내는지 정리했습니다."
      />

      <nav className="mb-8 flex flex-wrap gap-1.5">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="rounded-full bg-muted px-3.5 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-border/70 hover:text-foreground"
          >
            {s.title}
          </a>
        ))}
      </nav>

      <div className="space-y-10">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-20">
            <h2 className="mb-2 text-lg font-semibold">{s.title}</h2>
            {s.intro && <p className="mb-3 text-sm text-muted-foreground">{s.intro}</p>}
            {s.flow && (
              <div className="mb-4 rounded-md border border-border bg-muted/40 p-4">
                <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  정상 흐름
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {s.flow.happy.map((code, i) => (
                    <span key={code} className="flex items-center gap-1.5">
                      {i > 0 && <span className="text-muted-foreground">→</span>}
                      <span className="rounded-md bg-card px-2 py-1 text-xs font-semibold shadow-soft">
                        {ORDER_STATUS_LABELS[code]}
                      </span>
                    </span>
                  ))}
                </div>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                  {s.flow.branches.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            )}
            <TableShell>
              <TheadRow>
                <Th>상태</Th>
                <Th>이름</Th>
                <Th>의미</Th>
              </TheadRow>
              <tbody>
                {s.rows.map((r) => (
                  <BodyRow key={r.code}>
                    <Td className="whitespace-nowrap">
                      <span
                        className={`rounded-md px-2 py-0.5 font-mono text-xs ${TONE_CLASS[r.tone ?? 'default']}`}
                      >
                        {r.code}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap font-medium">{r.label}</Td>
                    <Td className="text-muted-foreground">{r.desc}</Td>
                  </BodyRow>
                ))}
              </tbody>
            </TableShell>
            {s.notes && (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {s.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </main>
  );
}
